import { ReadyPlayerMeService } from './readyPlayerMeService';
import { avatarService } from './avatarService';
import { UserAvatar } from '../types';

export class AvatarCallbackService {
  /**
   * Handle the callback from Ready Player Me when user returns to the app
   * This function should be called when the app detects URL parameters from Ready Player Me
   */
  static async handleAvatarCallback(
    urlParams: URLSearchParams,
    userId: string,
    teamId: string
  ): Promise<{ success: boolean; avatar?: UserAvatar; error?: string }> {
    try {
      // Extract avatar data from URL parameters
      const avatarId = urlParams.get('avatarId') || urlParams.get('id');
      const avatarUrl = urlParams.get('avatarUrl');
      const thumbnailUrl = urlParams.get('thumbnailUrl');
      const metadata = urlParams.get('metadata');

      if (!avatarId && !avatarUrl) {
        return { success: false, error: 'No avatar data found in URL parameters' };
      }

      // Extract avatar ID from URL if not directly provided
      let finalAvatarId = avatarId;
      if (!finalAvatarId && avatarUrl) {
        finalAvatarId = ReadyPlayerMeService.extractAvatarId(avatarUrl);
      }

      if (!finalAvatarId) {
        return { success: false, error: 'Could not extract avatar ID from URL' };
      }

      // Prepare avatar data
      const avatarData = {
        avatarId: finalAvatarId,
        avatarUrl: avatarUrl || ReadyPlayerMeService.getAvatarDisplayUrl(finalAvatarId),
        thumbnailUrl: thumbnailUrl || ReadyPlayerMeService.getAvatarDisplayUrl(finalAvatarId, { size: 256, quality: 'low' }),
        metadata: metadata ? JSON.parse(metadata) : undefined,
      };

      // Convert to UserAvatar format
      const userAvatarData = ReadyPlayerMeService.convertToUserAvatar(avatarData, userId, teamId);

      // Save to Firestore
      const savedAvatar = await avatarService.saveUserAvatar(userId, teamId, userAvatarData);

      return { success: true, avatar: savedAvatar };
    } catch (error) {
      console.error('Failed to handle avatar callback:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error occurred' 
      };
    }
  }

  /**
   * Check if current URL contains Ready Player Me callback parameters
   */
  static hasAvatarCallbackParams(): boolean {
    const urlParams = new URLSearchParams(window.location.search);
    return !!(urlParams.get('avatarId') || urlParams.get('id') || urlParams.get('avatarUrl'));
  }

  /**
   * Extract Ready Player Me parameters from current URL
   */
  static getAvatarCallbackParams(): URLSearchParams {
    return new URLSearchParams(window.location.search);
  }

  /**
   * Clear Ready Player Me parameters from URL without page reload
   */
  static clearAvatarCallbackParams(): void {
    const url = new URL(window.location.href);
    const paramsToRemove = ['avatarId', 'id', 'avatarUrl', 'thumbnailUrl', 'metadata'];
    
    paramsToRemove.forEach(param => {
      url.searchParams.delete(param);
    });

    window.history.replaceState({}, document.title, url.toString());
  }

  /**
   * Manual avatar save function for users who want to manually save their avatar
   * This can be called when users provide the Ready Player Me URL manually
   */
  static async saveAvatarFromUrl(
    avatarUrl: string,
    userId: string,
    teamId: string
  ): Promise<{ success: boolean; avatar?: UserAvatar; error?: string }> {
    try {
      // Validate the URL
      if (!ReadyPlayerMeService.isValidAvatarUrl(avatarUrl)) {
        return { success: false, error: 'Invalid Ready Player Me avatar URL' };
      }

      // Extract avatar ID
      const avatarId = ReadyPlayerMeService.extractAvatarId(avatarUrl);
      if (!avatarId) {
        return { success: false, error: 'Could not extract avatar ID from URL' };
      }

      // Prepare avatar data
      const avatarData = {
        avatarId,
        avatarUrl,
        thumbnailUrl: ReadyPlayerMeService.getAvatarDisplayUrl(avatarId, { size: 256, quality: 'low' }),
        metadata: undefined,
      };

      // Convert to UserAvatar format
      const userAvatarData = ReadyPlayerMeService.convertToUserAvatar(avatarData, userId, teamId);

      // Save to Firestore
      const savedAvatar = await avatarService.saveUserAvatar(userId, teamId, userAvatarData);

      return { success: true, avatar: savedAvatar };
    } catch (error) {
      console.error('Failed to save avatar from URL:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error occurred' 
      };
    }
  }
}
